import type { NavItem } from '../types';

/**
 * Investor Relations navigation, grouped by "jobs to be done" (ISS-440).
 *
 * The module used to expose all twelve destinations as a flat row of links,
 * which spilled across the top bar and gave no clue which surfaces belong
 * together. Each group below is a job an operator or investor actually comes
 * here to do; the group's own path is its landing surface, so clicking the
 * label still navigates rather than only opening the menu.
 *
 * Entries carry the same role gates as the routes they point at, so nobody is
 * shown a door they get bounced out of: investors never see the manager
 * surfaces, and admins never see the personal-portal pages that are reserved
 * for Owners and Investors. That gating is what removes the old duplication --
 * an owner used to see both the manager and the investor view of the library,
 * the updates and the consents as six separate top-level links.
 */
export const adminNavItems: NavItem[] = [
  {
    label: 'Dashboard',
    path: '/investors/admin',
    icon: 'LayoutDashboard',
    requireAdmin: true,
  },
  {
    // Job: know who owns what.
    label: 'Cap Table Management',
    path: '/investors/admin/cap-table',
    icon: 'Table2',
    requireAdmin: true,
    children: [
      { label: 'Cap Table', path: '/investors/admin/cap-table', icon: 'Table2', requireAdmin: true },
      { label: 'Investor Directory', path: '/investors/admin/investors', icon: 'Users', requireAdmin: true },
    ],
  },
  {
    // Job: tell investors what is happening and collect their signatures.
    label: 'Investor Reporting',
    path: '/investors/admin/updates',
    icon: 'Newspaper',
    requireAdmin: true,
    children: [
      { label: 'Updates Manager', path: '/investors/admin/updates', icon: 'Newspaper', requireAdmin: true },
      { label: 'Library Manager', path: '/investors/admin/library', icon: 'FolderOpen', requireAdmin: true },
      { label: 'Consent Manager', path: '/investors/admin/consents', icon: 'Vote', requireAdmin: true },
    ],
  },
];

export const investorNavItems: NavItem[] = [
  {
    // `/investors` dispatches admins to the admin dashboard, so for them this
    // entry is a second door onto "Dashboard" above. Investors are the only
    // role it actually lands somewhere different for.
    label: 'My Dashboard',
    path: '/investors',
    icon: 'LayoutDashboard',
    requiredRoles: ['Investor'],
  },
  {
    // Job: follow my own investment. Gated on the same roles the routes are —
    // an Admin who is not an Owner is bounced out of every page in here, so
    // listing them would be six dead links.
    label: 'My Investment',
    path: '/investors/updates',
    icon: 'Landmark',
    requiredRoles: ['Investor', 'Owner'],
    children: [
      { label: 'Updates', path: '/investors/updates', icon: 'Newspaper' },
      { label: 'Library', path: '/investors/library', icon: 'FolderOpen' },
      { label: 'Consents', path: '/investors/consents', icon: 'Vote' },
      { label: 'My Documents', path: '/investors/documents', icon: 'FileText' },
      { label: 'Profile', path: '/investors/profile', icon: 'User' },
    ],
  },
];

export const navItems: NavItem[] = [...adminNavItems, ...investorNavItems];
